/**
 * "Values that did not match" for step 4 (f10.7, design D10): unmapped Resource
 * BU values (f10.6's *Map to* control, reused) and rate keys with no forecast
 * line (*Keep* / *Exclude*, one at a time or all at once). Every rule and
 * version check is the server's; controls render only when
 * `capabilities.can_decide` says so.
 */

import { Ban, Check, CheckCheck, RotateCcw } from 'lucide-react';

import type { SalesRateElement, SalesRateReview } from '@/api/client';
import {
  useDecideAllSalesRateElements,
  useDecideSalesRateElement,
  useDecideSalesRateResourceBu,
} from '@/api/queries';

import { ResourceBuRow } from '../kimbleDump/UnmatchedKimbleCard';
import { RateCellView } from './SalesRatesCards';
import { MATCHED, UNDECIDED, decisionPill, refusalMessage } from './salesRatesView';

export function UnmatchedRatesCard({ cycleId, review }: { cycleId: string; review: SalesRateReview }) {
  const decideBu = useDecideSalesRateResourceBu(cycleId, review.batch.id);
  const decideElement = useDecideSalesRateElement(cycleId, review.batch.id);
  const decideAll = useDecideAllSalesRateElements(cycleId, review.batch.id);
  const bus = review.resource_bus.filter((item) => item.decision.code !== MATCHED);
  const elements = review.elements.filter((item) => item.decision.code !== MATCHED);
  const undecided = elements.filter((item) => item.decision.code === UNDECIDED).length;
  const canDecide = review.capabilities.can_decide;
  const error = decideBu.error ?? decideElement.error ?? decideAll.error;
  const busy = decideBu.isPending || decideElement.isPending || decideAll.isPending;
  const noLines = review.issues.items.some((item) => item.code === 'no_forecast_lines_yet');

  const bulk = (decision: 'KEEP' | 'EXCLUDE') =>
    decideAll.mutate({ decision, expected_batch_version: review.batch.version });

  return (
    <div className="card">
      <div className="card-head">
        <h3>Values that did not match</h3>
        {undecided > 0 ? (
          <span className="badge pill-warning">{undecided} rate key(s) block the commit</span>
        ) : bus.some((item) => item.decision.code === UNDECIDED) ? (
          <span className="badge pill-info">Resource BU left open</span>
        ) : (
          <span className="badge pill-success">All decided</span>
        )}
      </div>
      <div className="card-body stack">
        {error ? (
          <div className="alert alert-danger" role="alert">
            <div>
              <div className="a-body">{refusalMessage(error)}</div>
            </div>
          </div>
        ) : null}

        {bus.length === 0 && elements.length === 0 && (
          <p className="hint">
            All {review.resource_bus.length} Resource BU values mapped and all {review.elements.length} rate keys
            match a forecast line
          </p>
        )}

        {bus.length > 0 && (
          <div className="stack-sm">
            <div className="stat-label">Resource BU</div>
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th>Value in the report</th>
                    <th>Decision</th>
                    <th>Map to</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {bus.map((item) => (
                    <ResourceBuRow
                      key={item.id}
                      item={item}
                      review={review}
                      canDecide={canDecide}
                      busy={decideBu.isPending}
                      onDecide={(body) => decideBu.mutate({ itemId: item.id, body })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <p className="hint">
              An unmapped value keeps its rates under its raw spelling, the same key step 3 gives its line. A
              mapping you choose is added to the maintained table when you accept.
            </p>
          </div>
        )}

        {elements.length > 0 && (
          <div className="stack-sm">
            <div className="row">
              <div className="stat-label">Rate keys with no forecast line</div>
              {canDecide && undecided > 0 && (
                <div className="btn-row">
                  <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => bulk('KEEP')}>
                    <CheckCheck aria-hidden="true" /> Keep all
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => bulk('EXCLUDE')}>
                    <Ban aria-hidden="true" /> Exclude all
                  </button>
                </div>
              )}
            </div>
            {noLines && (
              <p className="hint">This cycle has no forecast lines yet — step 3 is not committed, so no key can match.</p>
            )}
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th>Rate key</th>
                    <th>Resource BU</th>
                    <th className="num">Annual rate</th>
                    <th>Decision</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {elements.map((item) => (
                    <ElementRow
                      key={item.id}
                      item={item}
                      canDecide={canDecide}
                      busy={busy}
                      onDecide={(decision) =>
                        decideElement.mutate({
                          itemId: item.id,
                          body: { decision, expected_version: item.version },
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <p className="hint">
              <strong>Keep</strong> stores the rates for a line that may appear later (a re-committed dump or a
              line added by hand). <strong>Exclude</strong> stores nothing for the key.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function ElementRow({
  item,
  canDecide,
  busy,
  onDecide,
}: {
  item: SalesRateElement;
  canDecide: boolean;
  busy: boolean;
  onDecide: (decision: 'KEEP' | 'EXCLUDE' | 'UNDECIDED') => void;
}) {
  const undecided = item.decision.code === UNDECIDED;
  return (
    <tr>
      <td className="mono">{item.element_key}</td>
      <td>
        {item.resource_bu_raw}
        {item.country && <span className="hint"> &middot; {item.country.code}</span>}
      </td>
      <td className="num">
        <RateCellView cell={item.annual} label={`${item.element_key} annual`} />
      </td>
      <td>
        <span className={decisionPill(item.decision.code)}>{item.decision.name}</span>
      </td>
      <td>
        {canDecide && (
          <div className="row-actions">
            {undecided ? (
              <>
                <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => onDecide('KEEP')}>
                  <Check aria-hidden="true" /> Keep
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={busy}
                  onClick={() => onDecide('EXCLUDE')}
                >
                  <Ban aria-hidden="true" /> Exclude
                </button>
              </>
            ) : (
              <button
                type="button"
                className="icon-btn"
                title="Back to awaiting decision"
                aria-label={`Undo decision for ${item.element_key}`}
                disabled={busy}
                onClick={() => onDecide('UNDECIDED')}
              >
                <RotateCcw aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}
